import { api } from "@/lib/api";

/**
 * Client-side upload engine. Mirrors worker/routes/editor.js exactly:
 *   - < 50MB  → single-shot presign+PUT+confirm
 *   - >= 50MB → multipart (initiate → sign-part loop → complete)
 *
 * SHIPPED 2026-07-21 (this file), replaces the earlier sequential-only path:
 *   1. Parallel part uploads — CONCURRENCY workers pull unclaimed part numbers
 *      from a shared counter. Saturates upstream bandwidth.
 *   2. Per-part retry with exponential backoff + jitter — MAX_PART_RETRIES
 *      attempts on transient failures (network / 5xx / 408 / 429).
 *   3. Re-sign on 403 — presigned URLs default to 1h TTL on R2, so any long
 *      upload can outlive its URL. On 403 we discard the URL and re-sign
 *      before the next retry.
 *   4. Resume-on-reload — session state (sessionId + completed parts) is
 *      persisted to localStorage keyed by file fingerprint. If the tab
 *      closes / crashes mid-upload, the user can re-add the same file and
 *      pick up where they left off. State is cleared on successful complete
 *      OR when the user explicitly discards it via clearResumeStateByKey().
 *
 * Deliberate scope cuts still in place:
 *   - No cross-tab coordination. If two tabs upload the same file
 *     simultaneously, they race for the same R2 parts (last-writer-wins).
 *   - Multi-file queue in src/pages/upload.tsx fires all files at once (no
 *     queue-level throttle). Combined with our 4-way part concurrency, a
 *     3-file drop uploads with 12 parallel PUTs. Fine on modern browsers
 *     (~6 sockets/host is the classic HTTP/1.1 limit but R2 is a separate
 *     host so it's fine, and CF is HTTP/2 anyway).
 */

// Keep these in sync with worker/routes/editor.js — MULTIPART_MIN_SIZE / MULTIPART_PART_SIZE.
const MULTIPART_MIN_SIZE = 50 * 1024 * 1024;
const MULTIPART_PART_SIZE = 16 * 1024 * 1024;

// Number of parts uploaded simultaneously. 4 saturates most consumer upstreams
// without blowing memory (peak in-flight = CONCURRENCY * MULTIPART_PART_SIZE
// = 64 MB). Bump to 6-8 only if we start supporting fiber-class customers.
const CONCURRENCY = 4;

// Per-part retry policy. Retries happen inside the pool worker so other parts
// keep flowing while one is backing off.
const MAX_PART_RETRIES = 5;
const RETRY_BASE_MS = 500;
const RETRY_JITTER_MS = 500;

// Stall watchdog — if no progress event fires for this many ms during a
// single PUT, treat the socket as dead and abort it so the retry loop can
// re-sign + try again. Fixes the "stuck at 85%" class of bugs where a TCP
// connection stays half-open (no bytes flowing but no error either) and the
// XHR would otherwise hang forever with no onerror to unblock us. Chosen
// generously enough that slow-but-alive connections (256 KB/s on a 16 MB
// part = ~64s total, ~30s between progress events is impossible) aren't
// killed by mistake — a real dial-up-slow connection still fires onprogress
// every few seconds.
const PART_STALL_TIMEOUT_MS = 30_000;

// LocalStorage schema version — bump if the ResumeState shape changes.
const RESUME_LS_PREFIX = "upload:resume:v1:";

// Resume window — must be <= worker's SESSION_TTL_MS (see worker/routes/editor.js).
// If localStorage has a session older than this, we purge it silently on next
// page load rather than offer a resume that would get rejected server-side with
// 410 Gone. Kept slightly tighter than the server (55 min vs 60 min) to protect
// against clock skew and the natural roundtrip lag between last activity and
// the user actually seeing the banner.
const RESUME_MAX_AGE_MS = 55 * 60 * 1000;

/** Matches worker/lib/versions.js MIME_TO_ASSET_TYPE exactly. */
export const ACCEPTED_MIME_TYPES = [
  "video/mp4",
  "audio/mpeg",
  "audio/wav",
  "audio/mp4",
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export function isAcceptedMime(type: string): boolean {
  return (ACCEPTED_MIME_TYPES as readonly string[]).includes(type);
}

// ─── Types ───────────────────────────────────────────────────────────────

export type UploadTarget = {
  projectId: string;
  parentVideoId?: string;
  /** Migration 0012 (2026-07-14) — omit / null = project root. */
  folderId?: string | null;
};

export type UploadResult = { id: string; rootVideoId: string; versionNumber: number };

type CompletedPart = { partNumber: number; etag: string };

type XhrPutResult = { etag: string | null; status: number };

/**
 * Persisted mid-upload state for a single file. `parts` accumulates as each
 * concurrent worker completes a chunk. If the tab closes with items in the
 * `parts` array we can pick up where we stopped.
 */
export type ResumeState = {
  v: 1;
  sessionId: string;
  assetType: string;
  contentType: string;
  fileName: string;
  fileSize: number;
  lastModified: number;
  projectId: string;
  parentVideoId?: string;
  folderId?: string | null;
  partSize: number;
  partCount: number;
  parts: CompletedPart[];
  createdAt: number;
  updatedAt: number;
};

// ─── Small helpers ───────────────────────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffMs(attempt: number): number {
  // attempt is 1-indexed. 1 → ~500ms, 2 → ~1s, 3 → ~2s, 4 → ~4s, 5 → ~8s.
  const base = RETRY_BASE_MS * Math.pow(2, attempt - 1);
  return base + Math.random() * RETRY_JITTER_MS;
}

/**
 * A response is transient-retryable if it's a network error (status 0),
 * a timeout / rate-limit (408, 429), or any 5xx server error. 403 is handled
 * separately as a re-sign case since it usually means the presigned URL
 * expired mid-upload.
 */
function isTransientStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

// ─── Resume state (localStorage) ─────────────────────────────────────────

/**
 * Fingerprint a File so we can match a re-selected File to a persisted
 * session on the next page load. Browser security prevents us from ever
 * storing the actual bytes, so the user always has to hand us the file
 * again — this key just tells us "yes, this is the same file."
 */
export function fingerprintFile(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function resumeKeyFor(fingerprint: string): string {
  return `${RESUME_LS_PREFIX}${fingerprint}`;
}

function safeGetLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function saveResumeState(state: ResumeState): void {
  const ls = safeGetLocalStorage();
  if (!ls) return;
  try {
    const fp = `${state.fileName}|${state.fileSize}|${state.lastModified}`;
    ls.setItem(resumeKeyFor(fp), JSON.stringify(state));
  } catch {
    // Quota exceeded or storage disabled — upload can still succeed, we just
    // lose the resume capability for this session. Not fatal.
  }
}

function loadResumeStateForFile(file: File, projectId: string): ResumeState | null {
  const ls = safeGetLocalStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(resumeKeyFor(fingerprintFile(file)));
    if (!raw) return null;
    const state = JSON.parse(raw) as ResumeState;
    // Sanity — refuse to resume against a different project (defensive: the
    // key already includes the fingerprint, but if the user drops the same
    // file into a different project, treat it as a fresh upload).
    if (
      state.v !== 1 ||
      state.fileName !== file.name ||
      state.fileSize !== file.size ||
      state.lastModified !== file.lastModified ||
      state.projectId !== projectId
    ) {
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

/**
 * Enumerate every stored resume state (for the "you have interrupted uploads" UI).
 *
 * Also purges (silently) any entry older than RESUME_MAX_AGE_MS so the banner
 * only shows states the server will still accept. Corrupt entries get cleaned
 * up too. This function is the single funnel through which the UI sees resume
 * candidates, so filtering here is enough — no separate garbage-collection pass.
 */
export function listResumeStates(): ResumeState[] {
  const ls = safeGetLocalStorage();
  if (!ls) return [];
  const results: ResumeState[] = [];
  const cutoff = Date.now() - RESUME_MAX_AGE_MS;
  // Two-pass so we can removeItem() safely without invalidating the key index.
  const toDelete: string[] = [];
  for (let i = 0; i < ls.length; i++) {
    const key = ls.key(i);
    if (!key || !key.startsWith(RESUME_LS_PREFIX)) continue;
    try {
      const raw = ls.getItem(key);
      if (!raw) { toDelete.push(key); continue; }
      const state = JSON.parse(raw) as ResumeState;
      if (state?.v !== 1) { toDelete.push(key); continue; }
      const activityTs = state.updatedAt ?? state.createdAt ?? 0;
      if (activityTs < cutoff) {
        toDelete.push(key);
        continue;
      }
      results.push(state);
    } catch {
      if (key) toDelete.push(key);
    }
  }
  for (const key of toDelete) {
    try { ls.removeItem(key); } catch { /* noop */ }
  }
  return results;
}

/** Discard the resume state under a specific fingerprint (user "Discard" click). */
export function clearResumeStateByFingerprint(fingerprint: string): void {
  const ls = safeGetLocalStorage();
  if (!ls) return;
  try {
    ls.removeItem(resumeKeyFor(fingerprint));
  } catch {
    /* noop */
  }
}

function clearResumeStateForFile(file: File): void {
  clearResumeStateByFingerprint(fingerprintFile(file));
}

// ─── In-flight registry (cancel + unload sweep) ──────────────────────────

type InflightHandle = {
  sessionId: string;
  fingerprint: string;
  abortController: AbortController;
  cancelled: boolean;
};

/**
 * Registry of every multipart upload currently in progress in this tab.
 * Used by two callers:
 *   1. cancelActiveUpload(sessionId) — user clicks the in-flight X button.
 *   2. abortAllInFlightOnUnload()    — beforeunload fires as the tab closes.
 *
 * Single-shot uploads (<50MB) never appear here — they either succeed in one
 * PUT or fail. There's no server-side session to abort.
 */
const activeSessions = new Map<string, InflightHandle>();

function registerActive(sessionId: string, fingerprint: string, abortController: AbortController) {
  activeSessions.set(sessionId, { sessionId, fingerprint, abortController, cancelled: false });
}
function unregisterActive(sessionId: string) {
  activeSessions.delete(sessionId);
}

/**
 * User-initiated cancel. Aborts every in-flight XHR for the session, fires
 * a fresh /multipart/abort so R2 releases the parts we already uploaded, and
 * clears the persisted resume state so the file doesn't reappear in the
 * banner.
 *
 * Returns a Promise so callers can await the network round-trip if they want,
 * but the cancel takes effect synchronously via AbortController.
 */
export async function cancelActiveUpload(sessionId: string): Promise<void> {
  const handle = activeSessions.get(sessionId);
  if (!handle) return;
  handle.cancelled = true;
  try { handle.abortController.abort(); } catch { /* noop */ }
  clearResumeStateByFingerprint(handle.fingerprint);
  activeSessions.delete(sessionId);
  try {
    await api.post("/editor/upload/multipart/abort", { sessionId });
  } catch {
    /* best effort — hourly sweep is the safety net */
  }
}

/**
 * Fire-and-forget cleanup on `beforeunload`. Aborts in-flight XHRs (releases
 * network sockets locally) but deliberately does NOT call /multipart/abort
 * on the server — closing the tab is a NON-TERMINAL action for resume to
 * work at all. If we aborted the session here, the resume banner would show
 * up on next visit and immediately fail with "Session is aborted" (400) on
 * the first sign-part call.
 *
 * Server-side cleanup for a user who never comes back is handled by:
 *   - 1h idle TTL — session.expiresAt bumps on every sign-part call; once
 *     it lapses, sign-part / complete return 410 Gone.
 *   - Hourly sweep cron — aborts the R2 multipart resource (frees storage
 *     bill) for any session past its TTL, worst-case ~2h after last activity.
 *
 * Intentional cancel (Ban icon on the upload row) still calls
 * `cancelActiveUpload` which DOES abort server-side + clear resume state —
 * that's the correct path for "I no longer want this upload."
 *
 * Behavior change 2026-07-23: previously fired /multipart/abort with
 * keepalive:true here, which contradicted the whole point of resume.
 */
export function abortAllInFlightOnUnload(): void {
  if (activeSessions.size === 0) return;
  for (const handle of activeSessions.values()) {
    try { handle.abortController.abort(); } catch { /* noop */ }
  }
  activeSessions.clear();
}

// ─── Duration extraction (parallel with upload) ──────────────────────────

/**
 * Read the intrinsic duration of a video/audio file client-side, using a
 * throwaway media element. Returns null for images, null on decode failure,
 * or if the duration is not a positive finite number. Migration 0014.
 */
async function readMediaDurationSeconds(file: File, assetType: string): Promise<number | null> {
  if (assetType !== "video" && assetType !== "audio") return null;
  return new Promise((resolve) => {
    const el = document.createElement(assetType === "audio" ? "audio" : "video");
    const url = URL.createObjectURL(file);
    let settled = false;
    const done = (value: number | null) => {
      if (settled) return;
      settled = true;
      el.src = "";
      try { el.removeAttribute("src"); el.load(); } catch { /* noop */ }
      URL.revokeObjectURL(url);
      resolve(value);
    };
    el.preload = "metadata";
    el.muted = true;
    el.onloadedmetadata = () => {
      const d = Number(el.duration);
      done(Number.isFinite(d) && d > 0 ? d : null);
    };
    el.onerror = () => done(null);
    el.src = url;
    window.setTimeout(() => done(null), 8000);
  });
}

// ─── Low-level PUT ───────────────────────────────────────────────────────

function xhrPut(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal
): Promise<XhrPutResult> {
  return new Promise((resolve, reject) => {
    // Short-circuit if the signal already fired before we even opened the XHR.
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted.", "AbortError"));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    // Stall watchdog. We track whether we killed the XHR ourselves (stall)
    // vs the external signal firing (user cancel) so onabort can distinguish
    // "retry this as a network error" from "propagate AbortError to the caller".
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const armStallTimer = () => {
      if (stallTimer != null) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        try { xhr.abort(); } catch { /* noop */ }
      }, PART_STALL_TIMEOUT_MS);
    };
    const clearStallTimer = () => {
      if (stallTimer != null) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    armStallTimer();

    const abortHandler = () => {
      try { xhr.abort(); } catch { /* noop */ }
    };
    signal?.addEventListener("abort", abortHandler, { once: true });

    xhr.upload.onprogress = (e) => {
      // Any progress event = the socket is still alive. Reset the watchdog.
      armStallTimer();
      onProgress(e.loaded, e.total || body.size);
    };
    xhr.onload = () => {
      clearStallTimer();
      signal?.removeEventListener("abort", abortHandler);
      resolve({ etag: xhr.getResponseHeader("ETag"), status: xhr.status });
    };
    // Distinguish network / abort from HTTP-level failures so the retry
    // decision can inspect status. status=0 marks the transport failed.
    xhr.onerror = () => {
      clearStallTimer();
      signal?.removeEventListener("abort", abortHandler);
      resolve({ etag: null, status: 0 });
    };
    xhr.onabort = () => {
      clearStallTimer();
      signal?.removeEventListener("abort", abortHandler);
      if (stalled) {
        // We killed it because no progress fired for PART_STALL_TIMEOUT_MS.
        // Present it to the retry layer as a transient network error so it
        // re-signs + retries with backoff — same path as a real onerror.
        resolve({ etag: null, status: 0 });
        return;
      }
      // Real abort from the caller's signal — propagate as AbortError so
      // the caller knows this was user-initiated and doesn't retry.
      reject(new DOMException("Upload aborted.", "AbortError"));
    };
    xhr.send(body);
  });
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ─── Part-level upload with retry + re-sign ──────────────────────────────

/**
 * Upload one part, retrying transient failures and re-signing when the
 * presigned URL expires. Progress is reported for THIS part's bytes only
 * (caller aggregates across parts).
 */
async function signAndUploadPartWithRetry(
  sessionId: string,
  partNumber: number,
  chunk: Blob,
  onProgress: (loaded: number) => void,
  signal: AbortSignal
): Promise<CompletedPart> {
  let presignedUrl: string | null = null;
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= MAX_PART_RETRIES + 1; attempt++) {
    if (signal.aborted) throw new DOMException("Upload cancelled.", "AbortError");

    // (Re)sign if we don't have a URL, or if the previous attempt was a 403
    // (URL expired), or a network error where the URL may have been consumed.
    if (!presignedUrl) {
      try {
        const signed = await api.post<{ ok: true; presignedUrl: string }>(
          "/editor/upload/multipart/sign-part",
          { sessionId, partNumber }
        );
        presignedUrl = signed.presignedUrl;
      } catch (err) {
        // Server said the session is gone — 410 Gone from the sign-part
        // endpoint covers both idle-timeout AND aborted state (backend
        // returns 410 with { expired: true } for both). Surface a fatal
        // error so we don't waste retries on a dead session; the catch
        // in uploadFile() will match on "upload session" and purge the
        // poisoned localStorage entry so the resume banner stops offering
        // this file.
        //
        // Other 4xx (400 for missing fields, 403 for permissions, 404 for
        // session not found) are unrecoverable bugs — throw with the raw
        // status. The localStorage entry stays; user will see the error
        // and can hit Discard to clear it.
        const status = (err as { status?: number })?.status ?? 0;
        if (status === 410) {
          throw new Error(
            "Upload session is no longer active. Please re-select the file to start over."
          );
        }
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          throw new Error(`sign-part failed (HTTP ${status})`);
        }
        lastError = err;
        if (attempt > MAX_PART_RETRIES) break;
        await sleep(backoffMs(attempt));
        continue;
      }
    }

    // Reset progress reporting for this attempt — XHR reports from 0 each PUT.
    // The pool worker will normalize this to (uploaded / totalPartSize) for the
    // outer progress function.
    let result: XhrPutResult;
    try {
      result = await xhrPut(presignedUrl, chunk, {}, (loaded) => onProgress(loaded), signal);
    } catch (err) {
      // AbortError bubbles up — cancel is not a retryable failure.
      if (isAbortError(err)) throw err;
      throw err;
    }

    if (result.status >= 200 && result.status < 300) {
      if (!result.etag) {
        throw new Error(
          `R2 returned no ETag for part ${partNumber}. The R2 bucket's CORS ` +
            "config likely needs ExposeHeaders: ['ETag'] added."
        );
      }
      // R2's S3 API wraps ETag in quotes; worker's multipart.complete() expects
      // the raw hex (see worker/lib/video.js). Strip once here.
      return { partNumber, etag: result.etag.replace(/^"|"$/g, "") };
    }

    // 403 = presigned URL expired (default TTL 1h). Discard and re-sign.
    if (result.status === 403) {
      presignedUrl = null;
      lastError = new Error(`Part ${partNumber} URL expired (403)`);
      if (attempt > MAX_PART_RETRIES) break;
      await sleep(backoffMs(attempt));
      continue;
    }

    // 5xx / 408 / 429 / network error — retry with backoff. Also re-sign,
    // since the URL might be single-use in some proxy paths.
    if (isTransientStatus(result.status)) {
      presignedUrl = null;
      lastError = new Error(`Part ${partNumber} transient failure (HTTP ${result.status})`);
      if (attempt > MAX_PART_RETRIES) break;
      await sleep(backoffMs(attempt));
      continue;
    }

    // Hard client error (4xx other than 403) — no point retrying, this is
    // almost always a bug in signing or a permanent auth failure.
    throw new Error(`Part ${partNumber} upload failed (HTTP ${result.status})`);
  }

  throw new Error(
    `Part ${partNumber} failed after ${MAX_PART_RETRIES} retries` +
      (lastError instanceof Error ? `: ${lastError.message}` : "")
  );
}

// ─── Concurrency pool ────────────────────────────────────────────────────

/**
 * Run parts through a fixed-size worker pool. Each worker pulls the next
 * unclaimed partNumber from a shared cursor, uploads it, records the result,
 * and updates the shared progress + persisted resume state.
 */
async function runPartsInParallel(args: {
  sessionId: string;
  file: File;
  partSize: number;
  partCount: number;
  alreadyDone: CompletedPart[];
  concurrency: number;
  signal: AbortSignal;
  onOverallProgress: (fraction: number) => void;
  onPartCompleted: (allCompletedSoFar: CompletedPart[]) => void;
}): Promise<CompletedPart[]> {
  const {
    sessionId,
    file,
    partSize,
    partCount,
    alreadyDone,
    concurrency,
    signal,
    onOverallProgress,
    onPartCompleted,
  } = args;

  // Per-part uploaded byte counter, indexed by (partNumber - 1). Any part
  // already known-complete counts as fully uploaded so the progress bar
  // starts wherever the resume left off, not from zero.
  const partBytes = new Array<number>(partCount).fill(0);
  const doneMap = new Map<number, string>();
  for (const p of alreadyDone) {
    const start = (p.partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    partBytes[p.partNumber - 1] = end - start;
    doneMap.set(p.partNumber, p.etag);
  }

  const reportOverall = () => {
    const uploaded = partBytes.reduce((a, b) => a + b, 0);
    onOverallProgress(uploaded / file.size);
  };
  reportOverall();

  // Work list = every part number not already complete.
  const workList: number[] = [];
  for (let pn = 1; pn <= partCount; pn++) {
    if (!doneMap.has(pn)) workList.push(pn);
  }

  let nextIdx = 0;
  const completed: CompletedPart[] = [...alreadyDone];

  const runWorker = async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const myIdx = nextIdx++;
      if (myIdx >= workList.length) return;
      const partNumber = workList[myIdx];
      const start = (partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);
      const chunkSize = end - start;

      const part = await signAndUploadPartWithRetry(sessionId, partNumber, chunk, (loaded) => {
        // Cap at chunk size to avoid transient overshoot from XHR events.
        partBytes[partNumber - 1] = Math.min(loaded, chunkSize);
        reportOverall();
      }, signal);

      partBytes[partNumber - 1] = chunkSize;
      completed.push(part);
      onPartCompleted([...completed]);
      reportOverall();
    }
  };

  // Kick off `concurrency` workers. If one throws, Promise.all rejects and
  // in-flight workers will resolve their current part before we abandon.
  // Any parts NOT yet claimed are simply never started — we leave the
  // multipart session open and rely on the resume state (or the daily
  // sweep on the worker) to clean up.
  const workers = Array.from({ length: Math.min(concurrency, workList.length || 1) }, () =>
    runWorker()
  );
  await Promise.all(workers);

  // Return parts sorted by partNumber (complete endpoint expects this).
  return completed.sort((a, b) => a.partNumber - b.partNumber);
}

// ─── Main entrypoint ─────────────────────────────────────────────────────

export type UploadOptions = {
  /**
   * Cancel signal. When it fires we abort in-flight XHRs, clear the persisted
   * resume state, and (for multipart) fire /multipart/abort so R2 releases
   * whatever parts had already landed.
   */
  signal?: AbortSignal;
  /**
   * Fires once, right after multipart initiate returns. Single-shot uploads
   * (<50MB) never fire this callback since they have no server-side session.
   * The caller can use this sessionId to build a Cancel button that calls
   * cancelActiveUpload(sessionId).
   */
  onSessionCreated?: (sessionId: string) => void;
};

/** onProgress receives a 0..1 fraction of THIS file's bytes uploaded. */
export async function uploadFile(
  file: File,
  target: UploadTarget,
  onProgress: (fraction: number) => void,
  opts: UploadOptions = {}
): Promise<UploadResult> {
  const contentType = file.type || "";
  if (!isAcceptedMime(contentType)) {
    throw new Error(
      `"${file.name}" isn't a supported type (${contentType || "unknown"}). Supported: MP4 video, MP3/WAV/M4A audio, JPEG/PNG/WEBP image.`
    );
  }

  const title = file.name.replace(/\.[^/.]+$/, "") || file.name;
  const signal = opts.signal;

  // ── Single-shot path (unchanged) ────────────────────────────────────
  if (file.size < MULTIPART_MIN_SIZE) {
    const presign = await api.post<{
      ok: true;
      presignedUrl: string;
      key: string;
      assetType: string;
      contentType: string;
    }>("/editor/upload/presign", {
      projectId: target.projectId,
      filename: file.name,
      contentType,
      fileSizeBytes: file.size,
    });

    // Read duration in parallel with the PUT so we don't add wall-clock time.
    const durationPromise = readMediaDurationSeconds(file, presign.assetType);

    // Simple retry for the single-shot path — most small files won't need it,
    // but a 60s wifi hiccup on a 40MB upload should recover.
    let lastResult: XhrPutResult | null = null;
    for (let attempt = 1; attempt <= MAX_PART_RETRIES + 1; attempt++) {
      if (signal?.aborted) throw new DOMException("Upload cancelled.", "AbortError");
      lastResult = await xhrPut(
        presign.presignedUrl,
        file,
        { "Content-Type": presign.contentType },
        (loaded, total) => onProgress(Math.min(loaded, total) / total),
        signal
      );
      if (lastResult.status >= 200 && lastResult.status < 300) break;
      if (!isTransientStatus(lastResult.status) && lastResult.status !== 403) break;
      if (attempt > MAX_PART_RETRIES) break;
      await sleep(backoffMs(attempt));
    }
    if (!lastResult || lastResult.status < 200 || lastResult.status >= 300) {
      throw new Error(
        `Upload failed (HTTP ${lastResult?.status ?? "?"}) after retries.`
      );
    }

    const durationSeconds = await durationPromise;

    const confirmed = await api.post<{ ok: true; id: string; rootVideoId: string; versionNumber: number }>(
      "/editor/upload/confirm",
      {
        projectId: target.projectId,
        key: presign.key,
        title,
        fileSizeBytes: file.size,
        assetType: presign.assetType,
        parentVideoId: target.parentVideoId,
        folderId: target.folderId ?? null,
        durationSeconds,
      }
    );
    onProgress(1);
    return confirmed;
  }

  // ── Multipart path (parallel + resume) ──────────────────────────────

  // If we have a persisted session for this exact file + project, resume it.
  // Otherwise initiate a fresh multipart upload.
  let resume = loadResumeStateForFile(file, target.projectId);

  if (!resume) {
    const initiate = await api.post<{
      ok: true;
      sessionId: string;
      partCount: number;
      assetType: string;
    }>("/editor/upload/multipart/initiate", {
      projectId: target.projectId,
      filename: file.name,
      fileSizeBytes: file.size,
      contentType,
      parentVideoId: target.parentVideoId,
    });

    resume = {
      v: 1,
      sessionId: initiate.sessionId,
      assetType: initiate.assetType,
      contentType,
      fileName: file.name,
      fileSize: file.size,
      lastModified: file.lastModified,
      projectId: target.projectId,
      parentVideoId: target.parentVideoId,
      folderId: target.folderId ?? null,
      partSize: MULTIPART_PART_SIZE,
      partCount: initiate.partCount,
      parts: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    saveResumeState(resume);
  }

  // Register in the in-flight map so cancelActiveUpload() and the beforeunload
  // sweep can reach this session. The AbortController owned here is the one
  // we plumb into every XHR + used to short-circuit the pool worker loop.
  const localController = new AbortController();
  const combinedSignal = signal
    ? mergeSignals(signal, localController.signal)
    : localController.signal;
  registerActive(resume.sessionId, fingerprintFile(file), localController);
  opts.onSessionCreated?.(resume.sessionId);

  // Kick duration decode in parallel with the part uploads; awaited before complete.
  const durationPromise = readMediaDurationSeconds(file, resume.assetType);

  try {
    const parts = await runPartsInParallel({
      sessionId: resume.sessionId,
      file,
      partSize: resume.partSize,
      partCount: resume.partCount,
      alreadyDone: resume.parts,
      concurrency: CONCURRENCY,
      signal: combinedSignal,
      onOverallProgress: (fraction) => onProgress(Math.min(fraction, 0.9999)),
      onPartCompleted: (allCompletedSoFar) => {
        resume!.parts = allCompletedSoFar;
        resume!.updatedAt = Date.now();
        saveResumeState(resume!);
      },
    });

    const durationSeconds = await durationPromise;

    const completed = await api.post<{ ok: true; id: string; rootVideoId: string; versionNumber: number }>(
      "/editor/upload/multipart/complete",
      {
        sessionId: resume.sessionId,
        title,
        parts,
        assetType: resume.assetType,
        parentVideoId: target.parentVideoId,
        folderId: target.folderId ?? null,
        durationSeconds,
      }
    );

    // Successful complete — clear the persisted state and the in-flight
    // registration. This is the only "clean exit" path.
    unregisterActive(resume.sessionId);
    clearResumeStateForFile(file);
    onProgress(1);
    return completed;
  } catch (err) {
    // Two shapes of failure:
    //   - AbortError from cancelActiveUpload / beforeunload / caller-passed
    //     signal → those callers already fired /multipart/abort + cleared
    //     resume state, so we just unregister and rethrow.
    //   - Any other error (network exhausted retries, 410 session expired,
    //     R2 error) → leave the resume state so the user can retry within
    //     the 1h window. The hourly sweep cleans up truly abandoned sessions.
    unregisterActive(resume.sessionId);
    if (isAbortError(err)) {
      // The abort path already cleaned up — nothing else to do here.
      throw err;
    }
    // If the error indicates the session is dead (410 from sign-part covers
    // both idle-timeout AND aborted state), purge the local state proactively
    // so the resume banner stops offering this file. See the 410 branch of
    // signAndUploadPartWithRetry for the exact error message we're matching.
    if (err instanceof Error && /upload session/i.test(err.message)) {
      clearResumeStateForFile(file);
    }
    throw err;
  }
}

/**
 * Combine an external AbortSignal with a local one so we can respect the
 * caller's cancel AND our own (used for cancelActiveUpload). Falls back to
 * a manual OR if AbortSignal.any isn't available (older browsers).
 */
function mergeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const anyFn = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === "function") return anyFn([a, b]);
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (a.aborted || b.aborted) controller.abort();
  a.addEventListener("abort", onAbort, { once: true });
  b.addEventListener("abort", onAbort, { once: true });
  return controller.signal;
}

/**
 * Explicit user-driven cancel: clear the persisted state AND best-effort
 * abort the R2 session so we don't leave orphans until the daily sweep.
 * Called from the upload page's "Discard" action on a resumable item.
 */
export async function discardResumableUpload(state: ResumeState): Promise<void> {
  clearResumeStateByFingerprint(
    `${state.fileName}|${state.fileSize}|${state.lastModified}`
  );
  try {
    await api.post("/editor/upload/multipart/abort", { sessionId: state.sessionId });
  } catch {
    /* best effort — sweep will clean up if this fails */
  }
}
